/**
 * TabErrorBoundary — wrap each CC tab so a single render crash doesn't
 * bin the whole app.
 *
 * Per Fable's CC scorecard C6 (2026-06-11): "No error boundaries, no
 * query caching, no tests (per inventory). Acceptable pre-launch;
 * schedule with B2." This ships the error-boundary half today so the
 * content-pipeline testing Nino's doing this weekend can't get
 * blocked by a tab crash.
 *
 * Behavior:
 *   - Catches any render error inside `children`
 *   - Shows a graceful fallback with the error message + a Reload tab
 *     button (which just re-mounts the children, clearing the error
 *     state) + a "Send to console" affordance for inspection
 *   - Logs the error + componentStack to console so devtools shows it
 *     even if the user closed the boundary banner
 *   - DOES NOT swallow the error silently — visibility is the whole
 *     point
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { captureException } from '@/lib/telemetry';

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback ("Learners" → "The Learners tab crashed"). */
  tabName?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export default class TabErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    // Always log — visibility is the point. The boundary catches the
    // render but devtools shouldn't lose the trace.
    console.error('[TabErrorBoundary]', error, info.componentStack);
    captureException(error);
  }

  reload = () => {
    this.setState({ error: null, componentStack: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="p-6 flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-destructive/15 border border-destructive/30 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground">
              {this.props.tabName ? `The ${this.props.tabName} tab crashed.` : 'This tab crashed.'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              The rest of the Command Centre is unaffected. Use the sidebar to navigate
              elsewhere, or reload this tab to try again.
            </p>
            <pre className="mt-3 px-3 py-2 rounded-md bg-background border border-border text-[11px] font-mono text-foreground overflow-auto max-h-48">
              {this.state.error.message}
              {this.state.componentStack ? `\n\n${this.state.componentStack}` : ''}
            </pre>
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={this.reload}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-border bg-card text-foreground hover:bg-accent"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reload this tab
              </button>
              <span className="text-[11px] text-muted-foreground">
                Full stack also logged to the browser console.
              </span>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
